import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import ApiService from '../services/ApiService';
import { obtenerProductoPorId } from '../services/productoService';
import { generateMetaPurchaseEventId, generateMetaPurchaseEventTime } from '../utils/metaPurchaseEventId';
import {
  metaCustomerTypeFromEsMayorista,
  metaEventNameFromCustomerType,
} from '../constants/metaPixel';
import { PRECIO_CONFIG } from '../constants';
import { setMpCheckoutIntentId } from '../utils/mpCheckoutSession';

const CarritoContext = createContext();

const toNumber = (val) => {
  const n = Number(val);
  return Number.isFinite(n) ? n : 0;
};

const aplicarPreciosEnItems = (itemsOrigen, preciosPorId) => {
  const eliminados = [];
  let preciosActualizados = false;

  const items = itemsOrigen.flatMap((item) => {
    const actual = preciosPorId.get(String(item.id));
    if (!actual) {
      eliminados.push(item);
      return [];
    }

    const precio = toNumber(actual.precio);
    const preciomenor = toNumber(actual.preciomenor);

    if (precio !== toNumber(item.precio) || preciomenor !== toNumber(item.preciomenor)) {
      preciosActualizados = true;
    }

    return [{
      ...item,
      precio,
      preciomenor,
      nombre: actual.nombre || item.nombre,
      imagenURL: actual.imagenURL || item.imagenURL,
    }];
  });

  return {
    items,
    preciosActualizados: preciosActualizados || eliminados.length > 0,
    eliminados,
  };
};

export const useCarrito = () => {
  const context = useContext(CarritoContext);
  if (!context) {
    throw new Error('useCarrito debe ser usado dentro de un CarritoProvider');
  }
  return context;
};

export const CarritoProvider = ({ children }) => {
  const [carritoItems, setCarritoItems] = useState(() => {
    const savedCarrito = localStorage.getItem('carrito');
    return savedCarrito ? JSON.parse(savedCarrito) : [];
  });
  const carritoItemsRef = useRef(carritoItems);
  carritoItemsRef.current = carritoItems;

  const [showCarritoSidebar, setShowCarritoSidebar] = useState(false);

  useEffect(() => {
    localStorage.setItem('carrito', JSON.stringify(carritoItems));
  }, [carritoItems]);

  const agregarAlCarrito = (producto) => {
    setCarritoItems(prev => {
      const existente = prev.find(item => item.id === producto.id);

      if (existente) {
        return prev.map(item =>
          item.id === producto.id
            ? {
                ...item,
                cantidad: item.cantidad + 1,
                // Al re-agregar, tomar precios vigentes del catálogo
                precio: toNumber(producto.precio),
                preciomenor: toNumber(producto.preciomenor),
                nombre: producto.nombre || item.nombre,
                imagenURL: producto.imagenURL || item.imagenURL,
              }
            : item
        );
      }

      return [...prev, {
        ...producto,
        precio: toNumber(producto.precio),
        preciomenor: toNumber(producto.preciomenor),
        cantidad: 1,
      }];
    });
    setShowCarritoSidebar(true);
  };

  const incrementarCantidad = (id) => {
    setCarritoItems(prev =>
      prev.map(item =>
        item.id === id ? { ...item, cantidad: item.cantidad + 1 } : item
      )
    );
  };

  const decrementarCantidad = (id) => {
    setCarritoItems(prev =>
      prev.map(item =>
        item.id === id && item.cantidad > 1
          ? { ...item, cantidad: item.cantidad - 1 }
          : item
      )
    );
  };

  const eliminarItem = (id) => {
    setCarritoItems(prev => prev.filter(item => item.id !== id));
  };

  const vaciarCarrito = () => {
    setCarritoItems([]);
  };

  /**
   * Aplica precios ya obtenidos (p. ej. respuesta 409 del backend) al carrito.
   */
  const aplicarPreciosRecibidos = useCallback((productosActualizados) => {
    const itemsOrigen = carritoItemsRef.current;
    if (!itemsOrigen.length || !Array.isArray(productosActualizados)) {
      return { items: itemsOrigen, preciosActualizados: false, eliminados: [] };
    }

    const preciosPorId = new Map(
      productosActualizados.map((producto) => [String(producto.id || producto.productoId), producto])
    );

    const resultado = aplicarPreciosEnItems(itemsOrigen, preciosPorId);
    setCarritoItems(resultado.items);
    return resultado;
  }, []);

  /**
   * Sincroniza precio/preciomenor del carrito con el catálogo actual.
   * Devuelve los ítems actualizados para poder usarlos de inmediato (p. ej. al checkout).
   */
  const refrescarPrecios = useCallback(async (itemsBase) => {
    const itemsOrigen = itemsBase ?? carritoItemsRef.current;

    if (!itemsOrigen.length) {
      return { items: [], preciosActualizados: false, eliminados: [] };
    }

    try {
      const resultados = await Promise.all(
        itemsOrigen.map(async (item) => {
          try {
            const actual = await obtenerProductoPorId(item.id);
            return { item, actual };
          } catch (error) {
            console.error(`Error al obtener producto ${item.id} para refrescar precio:`, error);
            return { item, actual: null, error: true };
          }
        })
      );

      if (resultados.some((resultado) => resultado.error)) {
        return {
          items: itemsOrigen,
          preciosActualizados: false,
          eliminados: [],
          error: 'No se pudieron actualizar los precios',
        };
      }

      const preciosPorId = new Map(
        resultados
          .filter(({ actual }) => actual)
          .map(({ item, actual }) => [String(item.id), actual])
      );

      // Productos no encontrados quedan fuera del map → se eliminan
      const resultado = aplicarPreciosEnItems(itemsOrigen, preciosPorId);
      setCarritoItems(resultado.items);
      return resultado;
    } catch (error) {
      console.error('Error al refrescar precios del carrito:', error);
      return {
        items: itemsOrigen,
        preciosActualizados: false,
        eliminados: [],
        error: error.message || 'No se pudieron actualizar los precios',
      };
    }
  }, []);

  const calcularTotales = (items) => {
    const cantidadTotalCalc = items.reduce((acc, item) => acc + item.cantidad, 0);
    const subtotalMinoristaCalc = items.reduce(
      (acc, item) => acc + (toNumber(item.preciomenor) * item.cantidad),
      0
    );
    const subtotalMayoristaCalc = items.reduce(
      (acc, item) => acc + (toNumber(item.precio) * item.cantidad),
      0
    );
    const esMayoristaCalc =
      subtotalMayoristaCalc >= PRECIO_CONFIG.MINIMO_MAYORISTA &&
      cantidadTotalCalc >= PRECIO_CONFIG.CANTIDAD_MINIMA_MAYORISTA;
    const montoTotal = esMayoristaCalc ? subtotalMayoristaCalc : subtotalMinoristaCalc;

    return {
      cantidadTotalCalc,
      subtotalMinoristaCalc,
      subtotalMayoristaCalc,
      esMayoristaCalc,
      montoTotal,
    };
  };

  const finalizarCompra = async (nombreContacto, telefonoContacto, metodoPago = 'mercadopago') => {
    try {
      if (!nombreContacto || !telefonoContacto || nombreContacto.trim() === '' || telefonoContacto.trim() === '') {
        return { success: false, error: 'Debe proporcionar nombre y teléfono de contacto' };
      }

      if (telefonoContacto.trim().length < 8) {
        return { success: false, error: 'El teléfono debe tener al menos 8 caracteres' };
      }

      const refresh = await refrescarPrecios();
      if (refresh.error) {
        return {
          success: false,
          error: 'No se pudieron verificar los precios actuales. Intentá nuevamente.',
        };
      }

      const itemsActualizados = refresh.items;
      if (!itemsActualizados.length) {
        return {
          success: false,
          error: refresh.eliminados.length
            ? 'Los productos de tu carrito ya no están disponibles.'
            : 'El carrito está vacío',
          preciosActualizados: refresh.preciosActualizados,
        };
      }

      if (refresh.eliminados.length > 0 || refresh.preciosActualizados) {
        return {
          success: false,
          preciosActualizados: true,
          error: refresh.eliminados.length
            ? 'Algunos productos ya no están disponibles o cambiaron de precio. Revisá el carrito e intentá de nuevo.'
            : 'Los precios de tu carrito se actualizaron. Revisá el nuevo total y volvé a confirmar la compra.',
        };
      }

      const productos = itemsActualizados.map(item => ({
        id: item.id,
        nombre: item.nombre,
        precio: toNumber(item.precio),
        preciomenor: toNumber(item.preciomenor),
        cantidad: item.cantidad,
      }));

      const { montoTotal, esMayoristaCalc } = calcularTotales(itemsActualizados);
      const metaCustomerType = metaCustomerTypeFromEsMayorista(esMayoristaCalc);
      const metaEventName = metaEventNameFromCustomerType(metaCustomerType);
      const metaEventId = generateMetaPurchaseEventId();
      const metaEventTime = generateMetaPurchaseEventTime();

      let respuesta;
      try {
        respuesta = await ApiService.post('/pedidos', {
          productos,
          montoTotal,
          descuento: 0,
          estado: 'CREADO',
          metodoPago,
          nombreContacto: nombreContacto.trim(),
          telefonoContacto: telefonoContacto.trim(),
          metaEventId,
          metaEventName,
          metaCustomerType,
          metaEventTime,
        });
      } catch (error) {
        // Backend detectó precios desactualizados: sincronizar UI y pedir confirmación
        if (error?.status === 409 && error?.payload?.code === 'PRECIOS_ACTUALIZADOS') {
          const data = error.payload.data || {};
          if (Array.isArray(data.productos)) {
            aplicarPreciosRecibidos(data.productos);
          } else {
            await refrescarPrecios();
          }
          return {
            success: false,
            preciosActualizados: true,
            error: error.payload.message
              || 'Los precios de tu carrito se actualizaron. Revisá el nuevo total y volvé a confirmar la compra.',
          };
        }
        throw error;
      }

      const initPoint = respuesta?.data?.init_point;
      const pedido = respuesta?.data?.pedido;
      const pedidoId = pedido?._id;
      const checkoutIntentId = respuesta?.data?.checkoutIntentId;
      const montoTotalPedido = toNumber(pedido?.montoNeto ?? pedido?.montoTotal ?? montoTotal);

      if (metodoPago === 'efectivo') {
        if (!pedidoId) {
          return {
            success: false,
            error: 'No se recibió la confirmación del pedido. Intentá nuevamente.',
          };
        }

        return {
          success: true,
          metodoPago: 'efectivo',
          pedidoId,
          montoTotal: montoTotalPedido,
          whatsappLink: respuesta?.data?.whatsappLink,
          metaEventId: pedido?.metaEventId || metaEventId,
          metaEventName: pedido?.metaEventName || metaEventName,
          metaCustomerType: pedido?.metaCustomerType || metaCustomerType,
          metaEventTime: pedido?.metaEventTime || metaEventTime,
        };
      }

      if (!initPoint || !checkoutIntentId) {
        return {
          success: false,
          error: 'No se recibió el enlace de pago de Mercado Pago. Intentá nuevamente.',
        };
      }

      setMpCheckoutIntentId(checkoutIntentId);
      window.location.href = initPoint;

      return {
        success: true,
        redirecting: true,
        checkoutIntentId,
        metaEventId,
        metaEventName,
        metaCustomerType,
        metaEventTime,
      };
    } catch (error) {
      console.error('Error al finalizar compra:', error);

      let mensajeError = 'Error al procesar la compra';
      let stockErrors = null;

      if (error?.payload) {
        mensajeError = error.payload.message || error.payload.error || error.payload.msg || JSON.stringify(error.payload);

        if (error.payload.details && Array.isArray(error.payload.details)) {
          stockErrors = {};
          error.payload.details.forEach(detail => {
            if (detail.productId && detail.error) {
              stockErrors[detail.productId] = detail.error;
            }
          });
        }
      } else if (error?.message) {
        mensajeError = error.message;
      }

      return { success: false, error: mensajeError, stockErrors };
    }
  };

  const cantidadTotal = carritoItems.reduce((total, item) => total + item.cantidad, 0);
  const subtotalMinorista = carritoItems.reduce((acc, item) => acc + (toNumber(item.preciomenor) * item.cantidad), 0);
  const subtotalMayorista = carritoItems.reduce((acc, item) => acc + (toNumber(item.precio) * item.cantidad), 0);
  const esMayorista =
    subtotalMayorista >= PRECIO_CONFIG.MINIMO_MAYORISTA &&
    cantidadTotal >= PRECIO_CONFIG.CANTIDAD_MINIMA_MAYORISTA;
  const total = esMayorista ? subtotalMayorista : subtotalMinorista;

  return (
    <CarritoContext.Provider value={{
      carritoItems,
      agregarAlCarrito,
      incrementarCantidad,
      decrementarCantidad,
      eliminarItem,
      vaciarCarrito,
      refrescarPrecios,
      finalizarCompra,
      cantidadTotal,
      subtotalMinorista,
      subtotalMayorista,
      total,
      esMayorista,
      showCarritoSidebar,
      setShowCarritoSidebar
    }}>
      {children}
    </CarritoContext.Provider>
  );
};
